import { useState, useSyncExternalStore } from 'react'
import { useMessages } from '@/features/language/use-language'
import { Notice } from '@/shared/ui/Notice'
import { useAppUpdate } from './use-app-update'

/** 이 값 아래로 내려가 있으면 목록을 읽는 중으로 본다. */
const TOP_THRESHOLD_PX = 8

/**
 * 새 자산이 나왔다는 것을 알리고 다시 열 자리를 주는 블록.
 *
 * 목록 흐름 안에 카드 한 장으로 선다. 스크롤하면 함께 올라가고 아래로 밀려 사라진다. 화면을
 * 덮거나 아래에 붙어 따라다니지 않는다.
 *
 * 닫기를 두지 않는다. 닫은 창은 새 자산을 받을 길을 잃고 그 상태로 몇 주를 살 수 있다. 재촉이
 * 되지 않는 일은 자리와 문구가 맡는다. 같은 자리에 같은 말로 한 번 서 있을 뿐이라 다른 화면에
 * 다녀와도 새로 묻는 것이 아니라 아까 있던 것이 그대로 있는 것으로 읽힌다.
 *
 * 공손한 알림 영역에 둔다. 화면과 함께 그려진 것은 읽히지 않고 나중에 선 것만 읽힌다. 이 알림은
 * 언제나 나중에 서는 쪽이다.
 */
export function AppUpdateNotice() {
  const m = useMessages()
  const { ready, reopening, reopen } = useAppUpdate()
  const shown = useShownOnceAtTop(ready)

  return (
    <div aria-live="polite" className="empty:hidden">
      {shown && (
        <Notice action={{ label: m.app.update.reopen, onClick: reopen, pending: reopening }}>
          {reopening ? m.app.update.reopening : m.app.update.message}
        </Notice>
      )}
    </div>
  )
}

function subscribeToScroll(onChange: () => void): () => void {
  window.addEventListener('scroll', onChange, { passive: true })
  return () => window.removeEventListener('scroll', onChange)
}

const readAtTop = () => window.scrollY <= TOP_THRESHOLD_PX

/**
 * 새 자산이 준비된 뒤 스크롤이 목록 맨 위에 있는 순간부터 참이 된다. 한 번 참이면 그대로 둔다.
 *
 * 읽는 도중에 세우지 않으려는 것이 이 조건이다. 이 자리가 화면 위쪽이라 읽는 중에 서면 읽던
 * 줄이 그만큼 아래로 밀린다.
 *
 * 한 번 세운 뒤에는 스크롤을 내려도 거두지 않는다. 거두면 아래에 있던 기록이 위로 당겨져
 * 읽던 자리가 같은 이유로 흔들린다.
 */
function useShownOnceAtTop(ready: boolean): boolean {
  // 서버 렌더가 없으므로 두 번째 인자만 쓰인다. 맨 위에서 시작한다고 보아 첫 판정을 맞춘다.
  const atTop = useSyncExternalStore(subscribeToScroll, readAtTop, () => true)

  const [shown, setShown] = useState(false)
  if (ready && atTop && !shown) setShown(true)

  return shown
}
